import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Styled from "../../../CompanyForm.styled";
import * as MUIStyled from "../../../MUIStyled";
import { getContactDetail, postContactDetail } from "../../../config/fetchAPI";
import {
    BackButton,
    ButtonPurpleNext,
} from "../../elements/Buttons/Buttons.js";
import CustomAlert from "../../elements/Alert/Alert";
import { emailValid } from "../../../config/validation";
import { DefaultLayout } from "../../common/Layout/DefaultLayout";
import ContactCard from "./ContactCard.js";
import ProgressBarStepper from "../../elements/ProgressBar/ProgressBar";
import {
    isTemporaryContactId,
    mapContactServerErrors,
} from "../../../utils/contactUtils.js";

export const ContactDetailPage = () => {
    const [contacts, setContacts] = useState([]);
    // eslint-disable-next-line
    const [updatedFormData, setUpdatedFormData] = useState([]);
    const [updatedContacts, setUpdatedContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const currentStep = 3;
    const totalSteps = 6;

    const [signatoryPrimarySelectedId, setSignatoryPrimarySelectedId] =
        useState(null);
    const [payrollPrimarySelectedId, setPayrollPrimarySelectedId] =
        useState(null);
    const [accountsPrimarySelectedId, setAccountsPrimarySelectedId] =
        useState(null);
    const [hrPrimarySelectedId, setHrPrimarySelectedId] = useState(null);

    // error messages
    const [contactErrors, setContactErrors] = useState({});
    const [primaryError, setPrimaryError] = useState("");
    const [maxAddedContactError, setMaxAddedContactError] = useState("");
    const [serverError, setServerError] = useState("");

    const navigate = useNavigate();

    const CONTACT_TYPES_PRIMARY = (() => {
        try {
            const associations =
                JSON.parse(sessionStorage.getItem("associations")) || {};
            const contactTypes = associations.contactTypes || [];

            const findIdByName = (name) =>
                contactTypes.find((ct) => ct.name === name)?.id;

            return {
                SIGNATORY: findIdByName("Signatory"), // id 2
                PAYROLL: findIdByName("Payroll"), // id 3
                ACCOUNTS: findIdByName("Accounts"), // id 4
                HR: findIdByName("Human Resources"), // id 1
            };
        } catch (e) {
            return {
                SIGNATORY: undefined,
                PAYROLL: undefined,
                ACCOUNTS: undefined,
                HR: undefined,
            };
        }
    })();

    useEffect(() => {
        handleGetContactDetail();
    }, []);

    useEffect(() => {
        if (
            Array.isArray(contacts) &&
            contacts.length > 0 &&
            updatedContacts.length === 0
        ) {
            // Only initialize updatedContacts from contacts when it's empty (first load)
            setUpdatedContacts(contacts);
        }
        // eslint-disable-next-line
    }, [contacts]);

    const handleGetContactDetail = async () => {
        try {
            let resultData = await getContactDetail();

            // If set up more than one contact with the same primaryTypes, remove the duplicates
            const usedTypes = new Set();
            const deduped = Array.isArray(resultData)
                ? resultData.map((contact) => {
                      const uniquePrimaryTypes = [];

                      (contact.primaryTypes || []).forEach((typeId) => {
                          if (!usedTypes.has(typeId)) {
                              usedTypes.add(typeId);
                              uniquePrimaryTypes.push(typeId);
                          }
                      });

                      return {
                          ...contact,
                          primaryTypes: uniquePrimaryTypes,
                      };
                  })
                : resultData;

            setContacts(deduped);
        } catch (error) {
            console.error("Error loading cached fields:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getCombinedContacts = (
        sourceContacts = contacts,
        sourceUpdatedContacts = updatedContacts
    ) => {
        const cleanedContacts = Array.isArray(sourceContacts)
            ? sourceContacts.filter((contact) => contact.id >= 10)
            : [];
        const combinedContacts = [...cleanedContacts];

        sourceUpdatedContacts.forEach((updated) => {
            const index = combinedContacts.findIndex(
                (contact) => contact.id === updated.id
            );
            if (index !== -1) {
                combinedContacts[index] = updated;
            } else {
                combinedContacts.push(updated);
            }
        });

        return combinedContacts;
    };

    const getContactsPayload = (combinedContacts) =>
        combinedContacts.map((contact) => ({ ...contact }));

    const handlePostContactDetail = async (
        sourceContacts = contacts,
        sourceUpdatedContacts = updatedContacts
    ) => {
        try {
            const combinedContacts = getCombinedContacts(
                sourceContacts,
                sourceUpdatedContacts
            );
            const contactsPayload = getContactsPayload(combinedContacts);
            const result = await postContactDetail(contactsPayload);

            // Check if server returned validation errors
            if (result && typeof result === "object") {
                let hasErrors = false;
                const serverFieldErrors = {};

                Object.keys(result).forEach((key) => {
                    const errorArray = result[key];
                    if (Array.isArray(errorArray) && errorArray.length > 0) {
                        errorArray.forEach((errorItem) => {
                            if (
                                errorItem.errors &&
                                typeof errorItem.errors === "object"
                            ) {
                                hasErrors = true;
                                const errorObj = errorItem.errors;
                                const contactIndex = parseInt(key);
                                const contact = combinedContacts[contactIndex];

                                if (contact) {
                                    const contactServerErrors =
                                        mapContactServerErrors(errorObj);

                                    if (
                                        Object.keys(contactServerErrors)
                                            .length > 0
                                    ) {
                                        serverFieldErrors[contact.id] =
                                            contactServerErrors;
                                    }
                                }
                            }
                        });
                    }
                });

                if (hasErrors) {
                    setContactErrors(serverFieldErrors);
                    setServerError(
                        "Please fix the validation errors before continuing."
                    );
                    return false;
                }
            }

            setServerError("");
            return true;
        } catch (error) {
            console.error("Error posting contact details:", error);
            setServerError("An error occurred while saving contact details.");
            return false;
        }
    };

    const handleSignatoryPrimaryChange = (id, isChecked) => {
        setSignatoryPrimarySelectedId(isChecked ? id : null);
    };

    const handlePayrollPrimaryChange = (id, isChecked) => {
        setPayrollPrimarySelectedId(isChecked ? id : null);
    };

    const handleAccountsPrimaryChange = (id, isChecked) => {
        setAccountsPrimarySelectedId(isChecked ? id : null);
    };

    const handleHrPrimaryChange = (id, isChecked) => {
        setHrPrimarySelectedId(isChecked ? id : null);
    };

    const handleFormDataChange = (value) => {
        // Only process if the value has a valid id
        if (!value || !value.id) {
            return;
        }

        setUpdatedFormData(value);

        // Update updatedContacts with the changed contact data
        setUpdatedContacts((prevUpdated) => {
            const existingIndex = prevUpdated.findIndex(
                (c) => c.id === value.id
            );
            if (existingIndex !== -1) {
                // Replace existing contact
                const newUpdated = [...prevUpdated];
                newUpdated[existingIndex] = value;
                return newUpdated;
            } else {
                // Add new contact
                return [...prevUpdated, value];
            }
        });
    };

    // add temporary id to contacts 1 to 10
    const getNextAvailableContactId = (contacts) => {
        if (!Array.isArray(contacts)) return 1;
        const ids1to10 = contacts
            .map((c) => c.id)
            .filter((id) => isTemporaryContactId(id));

        if (ids1to10.length === 0) return 1;
        const maxId = Math.max(...ids1to10);
        return maxId < 10 ? maxId + 1 : null;
    };

    const handleAddNewContact = async () => {
        const nextId = getNextAvailableContactId(contacts);
        if (!nextId) {
            setMaxAddedContactError("You can only add up to 10 contacts.");
            return;
        }

        const newContact = {
            id: nextId,
            name: "",
            lastname: "",
            phone: "",
            email: "",
            title: "",
            types: [],
            primaryTypes: [],
        };

        setContacts((prevContacts) => [...prevContacts, newContact]);
    };

    const handleNext = async () => {
        // Clear previous server errors
        setServerError("");

        // First validate client-side
        if (!validateForm()) {
            return;
        }

        // Then attempt to post and check for server validation errors
        const success = await handlePostContactDetail();
        if (!success) {
            return;
        }

        navigate("/payrollTerms");
    };

    const handleBack = () => {
        navigate("/trading");
    };

    const handleRemoveContact = async (id) => {
        if (!Array.isArray(contacts)) return;

        const filteredContacts = contacts.filter(
            (contact) => contact.id !== id
        );
        const filteredUpdatedContacts = updatedContacts.filter(
            (contact) => contact.id !== id
        );

        const success = await handlePostContactDetail(
            filteredContacts,
            filteredUpdatedContacts
        );

        if (!success) {
            return;
        }

        if (signatoryPrimarySelectedId === id) {
            setSignatoryPrimarySelectedId(null);
        }
        if (payrollPrimarySelectedId === id) {
            setPayrollPrimarySelectedId(null);
        }
        if (accountsPrimarySelectedId === id) {
            setAccountsPrimarySelectedId(null);
        }
        if (hrPrimarySelectedId === id) {
            setHrPrimarySelectedId(null);
        }

        setContacts(filteredContacts);
        setUpdatedContacts(filteredUpdatedContacts);
        setMaxAddedContactError("");
    };

    const validateForm = () => {
        let isValid = true;

        const fieldErrors = {}; // keyed by contact.id

        updatedContacts.forEach((contact) => {
            const errors = {};

            // First name validation
            if (!contact.name || contact.name.trim() === "") {
                errors.name = "First name is required.";
                isValid = false;
            } else if (contact.name.trim().length < 2) {
                errors.name = "First name must be at least 2 characters.";
                isValid = false;
            }

            // Last name validation
            if (!contact.lastname || contact.lastname.trim() === "") {
                errors.lastname = "Last name is required.";
                isValid = false;
            } else if (contact.lastname.trim().length < 2) {
                errors.lastname = "Last name must be at least 2 characters.";
                isValid = false;
            }

            // Email validation
            if (!contact.email || contact.email.trim() === "") {
                errors.email = "Email is required.";
                isValid = false;
            } else if (!emailValid(contact.email)) {
                errors.email = "Please enter a valid email address.";
                isValid = false;
            }

            // Phone validation
            if (!contact.phone || contact.phone.trim() === "") {
                errors.phone = "Phone number is required.";
                isValid = false;
            } else if (contact.phone.trim().length < 8) {
                errors.phone = "Phone number must be at least 8 digits.";
                isValid = false;
            }

            // Title validation
            if (!contact.title || contact.title.trim() === "") {
                errors.title = "Job title is required.";
                isValid = false;
            } else if (contact.title.trim().length < 2) {
                errors.title = "Job title must be at least 2 characters.";
                isValid = false;
            }

            // show errors by ID
            if (Object.keys(errors).length > 0) {
                fieldErrors[contact.id] = errors;
            }
        });

        // Validate required primary contact types
        const missingPrimaryTypes = [];

        if (!signatoryPrimarySelectedId) {
            missingPrimaryTypes.push("Authorised Signatory");
        }
        if (!payrollPrimarySelectedId) {
            missingPrimaryTypes.push("Payroll");
        }
        if (!accountsPrimarySelectedId) {
            missingPrimaryTypes.push("Accounts");
        }
        if (!hrPrimarySelectedId) {
            missingPrimaryTypes.push("HR");
        }

        if (missingPrimaryTypes.length > 0) {
            setPrimaryError(
                `Please select a primary contact for: ${missingPrimaryTypes.join(", ")}`
            );
            isValid = false;
        } else {
            setPrimaryError("");
        }

        setContactErrors(fieldErrors);
        return isValid;
    };

    return (
        <DefaultLayout currentStep={currentStep}>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <MUIStyled.CustomContainer>
                    <Styled.MainTitleArrowWrapper
                        style={{ marginBottom: "40px" }}>
                        <BackButton
                            onClick={handleBack}
                            sx={{ marginLeft: "10%" }}
                        />
                        <Styled.MainTitle style={{ margin: "8px 0 0 0" }}>
                            Contact details
                        </Styled.MainTitle>
                    </Styled.MainTitleArrowWrapper>

                    <Styled.BodyText
                        style={{ fontWeight: "400", marginBottom: "40px" }}>
                        Please ensure your company nominates at least one
                        Authorised Signatory, Accounts Payable and Payroll
                        contact to complete the setup form.
                    </Styled.BodyText>

                    <Styled.WrapperDiv>
                        <Styled.DynamicCardMainWrapper>
                            {Array.isArray(contacts) &&
                                contacts.map((contact, index) => (
                                    <ContactCard
                                        key={contact.id}
                                        contact={contact}
                                        contactTypesPrimary={
                                            CONTACT_TYPES_PRIMARY
                                        }
                                        onSetFormDataChange={
                                            handleFormDataChange
                                        }
                                        signatoryPrimarySelectedId={
                                            signatoryPrimarySelectedId
                                        }
                                        setSignatoryPrimarySelectedId={
                                            setSignatoryPrimarySelectedId
                                        }
                                        onSignatoryPrimaryChange={
                                            handleSignatoryPrimaryChange
                                        }
                                        payrollPrimarySelectedId={
                                            payrollPrimarySelectedId
                                        }
                                        setPayrollPrimarySelectedId={
                                            setPayrollPrimarySelectedId
                                        }
                                        onPayrollPrimaryChange={
                                            handlePayrollPrimaryChange
                                        }
                                        accountsPrimarySelectedId={
                                            accountsPrimarySelectedId
                                        }
                                        setAccountsPrimarySelectedId={
                                            setAccountsPrimarySelectedId
                                        }
                                        onAccountsPrimaryChange={
                                            handleAccountsPrimaryChange
                                        }
                                        hrPrimarySelectedId={
                                            hrPrimarySelectedId
                                        }
                                        setHrPrimarySelectedId={
                                            setHrPrimarySelectedId
                                        }
                                        onHrPrimaryChange={
                                            handleHrPrimaryChange
                                        }
                                        onRemoveContact={handleRemoveContact}
                                        canDeleteContact={index > 0}
                                        errors={contactErrors[contact.id] || {}}
                                    />
                                ))}
                        </Styled.DynamicCardMainWrapper>
                    </Styled.WrapperDiv>

                    {primaryError && <CustomAlert message={primaryError} />}
                    {maxAddedContactError && (
                        <CustomAlert message={maxAddedContactError} />
                    )}
                    {serverError && <CustomAlert message={serverError} />}

                    {contacts === "Error" && (
                        <MUIStyled.CustomAlert severity="error">
                            An internal error occurred, please contact support.
                        </MUIStyled.CustomAlert>
                    )}

                    <Styled.AdditionDetailBox
                        fontSize="16px"
                        style={{ marginTop: "20px" }}>
                        If you have more than one contact for authorised
                        signatory, payroll, accounts or HR, please
                        <Styled.AdditionDetailLink
                            onClick={handleAddNewContact}
                            fontSize="16px">
                            {" "}
                            add new contact
                        </Styled.AdditionDetailLink>
                        .
                    </Styled.AdditionDetailBox>

                    <MUIStyled.CustomGrid
                        container
                        width={"100%"}
                        justifyContent={{ xs: "center", sm: "flex-end" }}
                        alignSelf={{ xs: "center", sm: "flex-end" }}
                        marginRight={"25px"}>
                        <ButtonPurpleNext
                            type="submit"
                            onClick={handleNext}
                            disabled={contacts === "Error"}
                            style={{ marginTop: "40px" }}>
                            {isLoading ? (
                                <MUIStyled.CustomCircularProgress size={24} />
                            ) : (
                                "Next"
                            )}
                        </ButtonPurpleNext>
                    </MUIStyled.CustomGrid>
                </MUIStyled.CustomContainer>

                <Styled.ProgressBarContainerWrapper>
                    {currentStep <= totalSteps && (
                        <ProgressBarStepper
                            currentStep={currentStep}
                            totalSteps={totalSteps}
                        />
                    )}
                </Styled.ProgressBarContainerWrapper>
            </div>
        </DefaultLayout>
    );
};
